// F-008 + F-014 Part A: scent finder. Five questions, goal first → three picks scored against product metafields
// (goal: day/night, mood, accords, projection, rating; then leans, accords, season, inspired by), so new bottles join automatically. Scoring follows the
// concept's quizResults(). Opens from any link to "#scent-finder" (D-023).
(() => {
  const dialog = document.getElementById('MsQuiz');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const body = dialog.querySelector('[data-ms-quiz-body]');
  const count = dialog.querySelector('[data-ms-quiz-count]');
  const steps = [...dialog.querySelectorAll('.ms-quiz__steps i')];
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const threshold = Number(dialog.dataset.threshold) || 4500;
  const money = (cents) => new Intl.NumberFormat(undefined, { style: 'currency', currency: dialog.dataset.currency || 'USD' }).format(cents / 100);
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const norm = (s) => String(s || '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const list = (v) => (Array.isArray(v) ? v : []);

  // Goals (F-014): each maps to wear time, moods, accords, a target projection and, for some, a rating bonus.
  const QUIZ = [
    { key: 'goal', q: "What's it for?", sub: 'Pick the closest one.', opts: [
      { t: 'Smell good every day', d: 'Easy, likeable, all-round', short: 'Everyday pick', d2: ['Day', 'Day & Night'], m: ['Crowd-pleaser', 'Fresh & clean'], acc: ['Citrus', 'Aromatic', 'Woody'], p: 3.0, best: 1 },
      { t: 'Date night', d: 'Warm, close, memorable', short: 'Date night', d2: ['Night', 'Day & Night'], m: ['Sweet & playful', 'Mature & refined'], acc: ['Sweet', 'Amber', 'Spicy'], p: 3.4 },
      { t: 'The office', d: 'Clean, never too loud', short: 'Office-safe', d2: ['Day'], m: ['Fresh & clean', 'Crowd-pleaser'], acc: ['Citrus', 'Aromatic', 'Musky', 'Green'], p: 2.8 },
      { t: 'Night out or a party', d: 'Bold and noticeable', short: 'Night out', d2: ['Night'], m: ['Bold & unique', 'Sweet & playful'], acc: ['Sweet', 'Spicy', 'Fruity', 'Boozy'], p: 4.0 },
      { t: 'A special event', d: 'Polished and refined', short: 'Special occasion', d2: ['Night', 'Day & Night'], m: ['Mature & refined'], acc: ['Woody', 'Amber', 'Oud', 'Leather'], p: 3.4, rated: 1 },
      { t: 'Gym or hot weather', d: 'Light, fresh, cooling', short: 'Hot-weather fresh', s: ['Spring', 'Summer'], m: ['Fresh & clean'], acc: ['Citrus', 'Aquatic', 'Green', 'Aromatic'], p: 3.0 },
      { t: 'A gift', d: 'Safe, well-loved picks', short: 'Safe gift', m: ['Crowd-pleaser'], acc: [], p: 3.2, rated: 1, best: 1 } ] },
    { key: 'gender', q: 'Which way should it lean?', sub: "For you or someone else. Unisex scents always count.", opts: [
      { t: 'Masculine-leaning', v: 'Masculine' },
      { t: 'Feminine-leaning', v: 'Feminine' },
      { t: 'No preference', v: 'any' } ] },
    { key: 'family', multi: 2, q: 'What do you reach for?', sub: 'Pick up to two.', opts: [
      { t: 'Warm & sweet', d: 'Vanilla, amber, honey', g: ['#E8B04B', '#8A4E22'], acc: ['Sweet', 'Amber', 'Boozy'] },
      { t: 'Fresh & clean', d: 'Citrus, sea air, herbs', g: ['#8FD0DC', '#2E6FA8'], acc: ['Citrus', 'Aquatic', 'Green', 'Aromatic'] },
      { t: 'Spicy & bold', d: 'Pepper, cinnamon, cardamom', g: ['#F08A4B', '#9E2B2B'], acc: ['Spicy'] },
      { t: 'Smoky & woody', d: 'Oud, leather, cedar', g: ['#9C7A5B', '#2B221E'], acc: ['Woody', 'Leather', 'Smoky', 'Oud'] },
      { t: 'Fruity & bright', d: 'Pineapple, apple, berries', g: ['#F3CF5E', '#D9583A'], acc: ['Fruity'] },
      { t: 'Floral & soft', d: 'Rose, jasmine, musk', g: ['#E9A6C8', '#8E4A86'], acc: ['Floral', 'Musky'] },
      { t: 'Not sure', d: 'Go by what it’s for', none: true } ] },
    { key: 'weather', q: 'What weather?', opts: [
      { t: 'Warm months', short: 'Warm weather', s: ['Spring', 'Summer', 'All year'] },
      { t: 'Cold months', short: 'Cold weather', s: ['Fall', 'Winter', 'All year'] },
      { t: 'All year', s: [] } ] },
    { key: 'know', q: 'Love any of these already?', sub: "We'll point you to it, or to the closest thing we carry.", opts: [] },
  ];

  let step = 0;
  let answers = {};
  let picks = null;
  let catalog = null;
  let packs = []; // F-015 starter sets, suggested under the top three
  let loading = null;
  let advance = null; // pending auto-advance after a single-choice tap
  let trigger = null; // element that opened the quiz, for focus on close
  const live = dialog.querySelector('[data-ms-quiz-live]');
  // Announce inside the dialog: everything outside a modal dialog is inert to screen readers.
  const say = (text) => { live.textContent = ''; setTimeout(() => (live.textContent = text), 50); };
  const cancelAdvance = () => { clearTimeout(advance); advance = null; };
  // Re-render after the catalog loads only if the quiz is still open on the same step.
  const whenLoaded = () => { const at = step; load().then(() => { if (dialog.open && step === at) render(); }, failed); };
  const failed = () => { body.innerHTML = `<p class="ms-quiz__sub">We couldn't load the collection. <button type="button" class="ms-linkbtn" data-ms-quiz-retry>Try again</button></p>`; say("We couldn't load the collection."); };

  // Catalog JSON is fetched once, the first time the quiz opens.
  const load = () => (loading ||= fetch(dialog.dataset.url) // no JSON Accept header: Shopify answers that with a 404
    .then((r) => r.json())
    .then((data) => {
      packs = data.filter((f) => f.pk);
      catalog = data.filter((f) => !f.pk);
      // Question 4: the five originals with the most alternatives we sell (D-023). Most originals have one,
      // so ties go to originals with a best-selling alternative, then originals we also sell. Hybrids
      // ("A and B") are skipped: they read badly as a choice.
      const counts = {};
      const best = {};
      catalog.forEach((f) => {
        if (!f.ib || / and /.test(f.ib)) return;
        counts[f.ib] = (counts[f.ib] || 0) + 1;
        if (f.b) best[f.ib] = 1;
      });
      const sold = (ib) => (catalog.some((f) => isOriginalOf(f, ib)) ? 1 : 0);
      const top = Object.keys(counts)
        .sort((a, b) => counts[b] - counts[a] || (best[b] || 0) - (best[a] || 0) || sold(b) - sold(a) || a.localeCompare(b))
        .slice(0, 5);
      QUIZ.find((q) => q.key === 'know').opts = [
        ...top.map((ib) => ({ t: ib, d: `${counts[ib]} alternative${counts[ib] === 1 ? '' : 's'} here`, ib })),
        { t: 'None of these', d: 'Surprise me' },
      ];
    })
    .catch((error) => { loading = null; throw error; }));

  const pick = (key) => (answers[key] || []).map((i) => QUIZ.find((q) => q.key === key).opts[i]);
  const isOriginalOf = (f, ib) => norm(`${f.v} ${f.t}`) === norm(ib);

  function score() {
    const goal = pick('goal')[0];
    const g = pick('gender')[0].v;
    const fams = pick('family').filter((o) => !o.none);
    const wx = pick('weather')[0];
    const know = pick('know')[0];
    const related = know.ib ? catalog.filter((f) => f.ib === know.ib || isOriginalOf(f, know.ib)) : [];
    const knownAccords = new Set(related.flatMap((f) => list(f.a).slice(0, 4)));

    const inStock = (f) => Object.keys(f.z).filter((ml) => f.z[ml][2]).sort((a, b) => a - b);
    const scored = catalog.filter((f) => inStock(f).length).map((f) => {
      let s = 0;
      const why = [];
      const top = list(f.a).slice(0, 3);
      // An exact lean beats Unisex, so a masculine search isn't topped up with feminine-leaning "unisex" bottles.
      if (g === 'any' || f.g === g) s += 3; else if (f.g === 'Unisex') s += 1; else s -= 6;
      const fo = fams.filter((o) => o.acc.some((x) => top.includes(x)));
      if (fo.length) { s += 3 + fo.length * 1.5; fo.forEach((o) => why.push(o.t)); }
      // Goal: wear time, season, mood, accords, projection, and a rating/best-seller nudge for safe picks.
      let gs = 0;
      if (goal.d2 && goal.d2.some((x) => list(f.d).includes(x))) gs += 1.5;
      if (goal.s && goal.s.some((x) => list(f.s).includes(x))) gs += 1.5;
      if (goal.m.some((x) => list(f.m).includes(x))) gs += 1.5;
      if (goal.acc.some((x) => top.includes(x))) gs += 1;
      if (f.p) gs += Math.max(-1, 1 - Math.abs(f.p - goal.p) * 1.5);
      if (goal.rated && f.r) gs += Math.max(0, (f.r - 4) * 3);
      if (goal.best && f.b) gs += 0.5;
      s += gs;
      if (gs >= 3) why.unshift(goal.short);
      if (wx.s.length && wx.s.some((x) => list(f.s).includes(x))) { s += 2; why.push(wx.short); }
      if (know.ib && f.ib === know.ib) { s += 5; why.unshift(`Like ${know.t}`); }
      else if (know.ib && isOriginalOf(f, know.ib)) { s += 5; why.unshift('The original'); }
      else if (know.ib && list(f.a).slice(0, 4).filter((x) => knownAccords.has(x)).length >= 2) s += 1.5;
      return { f, s, why: [...new Set(why)].slice(0, 3) };
    }).sort((a, b) => b.s - a.s);

    // Pack: the one whose scents score best on average for these answers, preferring packs that hold a top-three pick.
    const byHandle = Object.fromEntries(scored.map((r) => [r.f.h, r.s]));
    const top3 = scored.slice(0, 3).map((r) => r.f.h);
    const pack = packs.map((k) => ({ k, s: k.pk.reduce((a, h) => a + (byHandle[h] ?? -6), 0) / k.pk.length + k.pk.filter((h) => top3.includes(h)).length,
      hits: k.pk.filter((h) => top3.includes(h)).length })).sort((a, b) => b.s - a.s)[0];
    if (pack) {
      // Each starter at the set's size, or its smallest size in stock.
      pack.items = pack.k.pk.map((h) => catalog.find((f) => f.h === h)).filter(Boolean).map((f) => {
        const want = pack.k.sz?.[f.h] ?? pack.k.ml;
        const ml = f.z[want]?.[2] ? String(want) : inStock(f)[0];
        return ml && { f, ml };
      }).filter(Boolean);
      pack.total = pack.items.reduce((sum, x) => sum + x.f.z[x.ml][1], 0);
    }

    const max = 3 + 6 + 6 + 2 + 5;
    return { pack, list: scored.slice(0, 3).map((r) => ({ ...r, sizes: inStock(r.f), sel: inStock(r.f)[0], pct: Math.max(60, Math.min(98, Math.round(55 + (r.s / max) * 45))) })) };
  }

  function setProgress() {
    const done = step >= QUIZ.length;
    steps.forEach((el, i) => el.classList.toggle('is-done', i < step));
    count.textContent = done ? 'Your matches' : `Question ${step + 1} of ${QUIZ.length}`;
  }

  function focusHeading() {
    const h = body.querySelector('h2');
    if (h) h.focus({ preventScroll: true });
    body.scrollTop = 0;
    dialog.scrollTop = 0;
  }

  function renderQuestion() {
    const q = QUIZ[step];
    if (!q.opts.length) {
      body.innerHTML = '<p class="ms-quiz__sub">Loading the collection…</p>';
      whenLoaded();
      return;
    }
    const sel = answers[q.key] || [];
    body.innerHTML = `<h2 tabindex="-1">${esc(q.q)}</h2><p class="ms-quiz__sub">${esc(q.sub || 'Tap one to continue.')}</p>
      <div class="ms-quiz__opts">${q.opts.map((o, i) => `<button type="button" class="ms-quiz__opt${o.g ? ' ms-quiz__opt--grad' : ''}" data-ms-quiz-opt="${i}" aria-pressed="${sel.includes(i)}" style="--delay:${i * 45}ms${o.g ? `;--g1:${o.g[0]};--g2:${o.g[1]}` : ''}"><b>${esc(o.t)}</b>${o.d && typeof o.d === 'string' ? `<small>${esc(o.d)}</small>` : ''}</button>`).join('')}</div>
      <div class="ms-quiz__foot"><button type="button" class="ms-quiz__back" data-ms-quiz-back ${step ? '' : 'hidden'}>← Back</button>${q.multi ? `<button type="button" class="ms-quiz__next" data-ms-quiz-next ${sel.length ? '' : 'disabled'}>Next</button>` : ''}</div>`;
    focusHeading();
  }

  function renderResults(keepFocus) {
    if (!catalog) {
      body.innerHTML = '<p class="ms-quiz__sub">Finding your matches…</p>';
      whenLoaded();
      return;
    }
    if (!picks) picks = score();
    const n = picks.list.length;
    if (!n) {
      body.innerHTML = `<h2 tabindex="-1">Nothing matches right now</h2><p class="ms-quiz__sub">Try different answers, or browse the whole collection.</p>
        <div class="ms-quiz__foot"><button type="button" class="ms-quiz__back" data-ms-quiz-back>← Back</button><a class="ms-quiz__next" href="/collections/all">Shop all</a></div>`;
      return focusHeading();
    }
    const word = ['', 'one', 'two', 'three'][n];
    const total = picks.list.reduce((sum, p) => sum + p.f.z[p.sel][1], 0);
    const left = threshold - total;
    const sameSize = picks.list.every((p) => p.sel === picks.list[0].sel);
    body.innerHTML = `<h2 tabindex="-1">Your top <em>${word}</em></h2>
      <p class="ms-quiz__sub">Matched on your answers. Start with 1 ml to try it, or pick a bigger size.</p>
      <ol class="ms-quiz__results" role="list">${picks.list.map((p, i) => `<li class="ms-quiz__pick" style="--delay:${i * 110}ms">
        <a class="ms-quiz__img" href="${esc(p.f.u)}" tabindex="-1" aria-hidden="true">${p.f.i ? `<img src="${esc(p.f.i)}" alt="" width="150" height="150" loading="lazy">` : ''}</a>
        <span class="ms-quiz__match">${p.pct}% match</span>
        <p class="ms-quiz__house">${esc(p.f.v)}</p>
        <h3 class="ms-quiz__name"><a href="${esc(p.f.u)}">${esc(p.f.t)}</a></h3>
        ${p.f.ib ? `<p class="ms-quiz__insp">Inspired by <b>${esc(p.f.ib)}</b></p>` : ''}
        ${p.why.length ? `<ul class="ms-quiz__whys" role="list" aria-label="Why it matches">${p.why.map((w) => `<li>${esc(w)}</li>`).join('')}</ul>` : ''}
        <div class="ms-quiz__sizes" role="group" aria-label="Size">${p.sizes.map((ml) => `<button type="button" class="ms-quiz__size" data-ms-quiz-size="${i}:${ml}" aria-pressed="${ml === p.sel}">${ml} ml</button>`).join('')}</div>
        <button type="button" class="ms-quiz__add" data-ms-quiz-add="${i}">Add ${p.sel} ml · ${money(p.f.z[p.sel][1])}</button>
      </li>`).join('')}</ol>
      <div class="ms-quiz__sum"><div><b>${n === 1 ? 'Your match' : `All ${word}`}${sameSize ? ` as ${picks.list[0].sel} ml` : ''}</b><small>${money(total)} · ${left <= 0 ? 'ships free with a free sample' : `${money(left)} more for free shipping and a free sample`}</small></div>
        <button type="button" class="ms-quiz__next" data-ms-quiz-all>${n === 1 ? 'Add to bag' : `Add all ${word}`}</button></div>
      ${picks.pack?.items.length ? `<div class="ms-quiz__pack">
        <a class="ms-quiz__packimg" href="${esc(picks.pack.k.u)}" tabindex="-1" aria-hidden="true">${picks.pack.items[0]?.f.i ? `<img src="${esc(picks.pack.items[0].f.i)}" alt="" width="96" height="96" loading="lazy">` : ''}</a>
        <div><b>Or start with a set: <a href="${esc(picks.pack.k.u)}">${esc(picks.pack.k.t)}</a></b><small>${picks.pack.items.length} scents${picks.pack.items.every((x) => x.ml === picks.pack.items[0].ml) ? ` as ${picks.pack.items[0].ml} ml` : ''}${picks.pack.hits ? `, including ${['', 'one', 'two', 'three'][picks.pack.hits]} of your matches` : ''} · ${money(picks.pack.total)}${picks.pack.total >= threshold ? ' · ships free with a free sample' : ''}. Swap any of them on the set page.</small></div>
        <button type="button" class="ms-quiz__add" data-ms-quiz-pack>Add the set</button></div>` : ''}
      <div class="ms-quiz__foot"><button type="button" class="ms-quiz__back" data-ms-quiz-retake>↺ Retake the quiz</button></div>`;
    if (!keepFocus) focusHeading();
  }

  function render() {
    setProgress();
    if (step >= QUIZ.length) renderResults(); else renderQuestion();
  }

  function open(from) {
    cancelAdvance();
    if (from) trigger = from;
    step = 0;
    answers = {};
    picks = null;
    render();
    if (!dialog.open) { dialog.showModal(); document.body.classList.add('overflow-hidden'); }
    focusHeading();
    load().catch(() => {});
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  async function add(items, button, { all } = {}) {
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      await msCart.request(window.routes.cart_add_url + '.js', { items });
      if (all) {
        close();
        msCart.drawer()?.open();
      } else {
        button.textContent = 'Added ✓';
        say('Added to your bag.');
      }
      return true;
    } catch (error) {
      button.disabled = false;
      button.textContent = label;
      say(error.message || "That didn't go through. Try again.");
      return false;
    }
  }

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target.closest('[data-ms-quiz-close]')) return close();
    const opt = event.target.closest('[data-ms-quiz-opt]');
    if (opt) {
      const q = QUIZ[step];
      const i = Number(opt.dataset.msQuizOpt);
      if (q.multi) {
        let sel = answers[q.key] || [];
        // "Not sure" stands alone: it clears the others, and picking another clears it.
        if (q.opts[i].none) sel = sel.includes(i) ? [] : [i];
        else sel = sel.includes(i) ? sel.filter((x) => x !== i) : [...sel.filter((x) => !q.opts[x].none), i].slice(-q.multi);
        answers[q.key] = sel;
        body.querySelectorAll('[data-ms-quiz-opt]').forEach((b) => b.setAttribute('aria-pressed', sel.includes(Number(b.dataset.msQuizOpt))));
        body.querySelector('[data-ms-quiz-next]').disabled = !sel.length;
      } else {
        if (advance) return; // a second quick tap must not skip the next question
        answers[q.key] = [i];
        body.querySelectorAll('[data-ms-quiz-opt]').forEach((b) => b.setAttribute('aria-pressed', b === opt));
        advance = setTimeout(() => { advance = null; step++; render(); }, reduce ? 0 : 280);
      }
      return;
    }
    if (event.target.closest('[data-ms-quiz-next]') && !picks) { step++; return render(); }
    if (event.target.closest('[data-ms-quiz-back]')) { cancelAdvance(); step = Math.max(0, step - 1); picks = null; return render(); }
    if (event.target.closest('[data-ms-quiz-retake]')) return open();
    if (event.target.closest('[data-ms-quiz-retry]')) return render();
    const sz = event.target.closest('[data-ms-quiz-size]');
    if (sz) {
      const [i, ml] = sz.dataset.msQuizSize.split(':');
      const p = picks.list[Number(i)];
      if (p.sel !== ml) { p.sel = ml; p.added = false; renderResults(true); body.querySelector(`[data-ms-quiz-size="${i}:${ml}"]`)?.focus(); }
      return;
    }
    const one = event.target.closest('[data-ms-quiz-add]');
    if (one) {
      const p = picks.list[Number(one.dataset.msQuizAdd)];
      return add([{ id: p.f.z[p.sel][0], quantity: 1 }], one).then((ok) => { if (ok) p.added = true; });
    }
    const all = event.target.closest('[data-ms-quiz-all]');
    if (all) {
      // Picks already added one by one aren't added twice.
      const rest = picks.list.filter((p) => !p.added);
      if (!rest.length) { close(); msCart.drawer()?.open(); return; }
      add(rest.map((p) => ({ id: p.f.z[p.sel][0], quantity: 1 })), all, { all: true });
    }
    const pk = event.target.closest('[data-ms-quiz-pack]');
    if (pk && picks.pack?.items.length) add(picks.pack.items.map((x) => ({ id: x.f.z[x.ml][0], quantity: 1 })), pk, { all: true });
  });

  dialog.addEventListener('close', () => {
    cancelAdvance();
    document.body.classList.remove('overflow-hidden');
    // The opener may sit in a menu that closed; fall back to the menu toggle.
    const target = trigger && trigger.offsetParent ? trigger : document.querySelector('header-drawer summary');
    target?.focus();
    if (location.hash === '#scent-finder') history.replaceState(null, '', location.pathname + location.search);
  });

  // Links to #scent-finder open the quiz in place instead of navigating.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#scent-finder"]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    link.closest('details[open]')?.querySelector('summary')?.click(); // e.g. the mobile menu
    open(link);
  });
  const fromHash = () => { if (location.hash === '#scent-finder') open(); };
  window.addEventListener('hashchange', fromHash);
  fromHash();
})();
